import { useState, useMemo, useEffect } from "react"
import { useParams } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { apiTenant, ApiError } from "../lib/api"

interface Employee {
  id: string
  employeeNo: string
  fullName: string
  status: string
}

interface AttendanceRecord {
  id: string
  employeeId: string
  date: string
  clockIn: string | null
  clockOut: string | null
  breakMinutes: number
  hoursWorked: string
  regularHours: string
  overtimeHours: string
  holidayOvertimeHours: string
  isSunday: boolean
  isHoliday: boolean
  status: "present" | "absent" | "late" | "half_day"
  notes: string | null
}

const ATT_STATUS_PILL: Record<string, string> = {
  present: "bg-emerald-100 text-emerald-700",
  late: "bg-amber-100 text-amber-700",
  half_day: "bg-blue-100 text-blue-700",
  absent: "bg-red-100 text-red-700",
}

function pad(n: number): string {
  return String(n).padStart(2, "0")
}


function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate()
}

export function AttendancePage() {
  const { slug = "" } = useParams<{ slug: string }>()
  const qc = useQueryClient()
  const today = new Date()

  const [employeeId, setEmployeeId] = useState<string>("")
  const [year, setYear] = useState<number>(today.getFullYear())
  const [month, setMonth] = useState<number>(today.getMonth() + 1)
  const [editDate, setEditDate] = useState<string | null>(null)
  const [showImport, setShowImport] = useState(false)

  const employees = useQuery({
    queryKey: ["employees-active", slug],
    enabled: !!slug,
    queryFn: () => apiTenant<{ employees: Employee[] }>(slug, "/employees?status=active"),
  })

  // Auto-select first employee when list arrives.
  useEffect(() => {
    if (!employeeId && employees.data && employees.data.employees.length > 0) {
      setEmployeeId(employees.data.employees[0].id)
    }
  }, [employees.data, employeeId])

  const monthStart = `${year}-${pad(month)}-01`
  const monthEnd = `${year}-${pad(month)}-${pad(daysInMonth(year, month))}`

  const attendance = useQuery({
    queryKey: ["attendance", slug, employeeId, year, month],
    enabled: !!slug && !!employeeId,
    queryFn: () =>
      apiTenant<{ attendance: AttendanceRecord[] }>(
        slug,
        `/attendance?employeeId=${employeeId}&from=${monthStart}&to=${monthEnd}&limit=500`,
      ),
  })

  const byDate = useMemo(() => {
    const map = new Map<string, AttendanceRecord>()
    for (const r of attendance.data?.attendance ?? []) {
      map.set(r.date.slice(0, 10), r)
    }
    return map
  }, [attendance.data])

  const totals = useMemo(() => {
    const items = attendance.data?.attendance ?? []
    return {
      records: items.length,
      hours: items.reduce((s, r) => s + Number(r.hoursWorked), 0),
      overtime: items.reduce((s, r) => s + Number(r.overtimeHours), 0),
      holidayOt: items.reduce((s, r) => s + Number(r.holidayOvertimeHours), 0),
    }
  }, [attendance.data])

  const monthYears: number[] = []
  for (let y = today.getFullYear() + 1; y >= today.getFullYear() - 5; y--) monthYears.push(y)

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["attendance", slug, employeeId] })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Attendance</h1>
          <p className="text-xs text-slate-500 mt-1">
            Tenant <span className="font-mono">{slug}</span>
          </p>
        </div>
        <button
          onClick={() => setShowImport(true)}
          className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded"
        >
          Bulk import (CSV)
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <select
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            className="px-2 py-1 border border-slate-300 rounded text-sm bg-white"
          >
            <option value="">Pick employee…</option>
            {employees.data?.employees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.employeeNo} · {e.fullName}
              </option>
            ))}
          </select>
          <div className="ml-auto flex items-center gap-2">
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="px-2 py-1 border border-slate-300 rounded text-sm bg-white"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(2000, i, 1).toLocaleString("en", { month: "long" })}
                </option>
              ))}
            </select>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="px-2 py-1 border border-slate-300 rounded text-sm bg-white"
            >
              {monthYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Summary strip */}
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex gap-4 text-xs text-slate-600">
          <span>
            {totals.records} day{totals.records === 1 ? "" : "s"} recorded
          </span>
          <span>
            Hours: <strong className="font-mono text-slate-900">{totals.hours.toFixed(1)}</strong>
          </span>
          <span>
            OT: <strong className="font-mono text-slate-900">{totals.overtime.toFixed(1)}</strong>
          </span>
          <span>
            Holiday OT: <strong className="font-mono text-slate-900">{totals.holidayOt.toFixed(1)}</strong>
          </span>
        </div>

        {!employeeId && <div className="p-8 text-center text-sm text-slate-400">Pick an employee to view attendance.</div>}

        {employeeId && attendance.isLoading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
        {attendance.error && <div className="p-6 text-sm text-red-600">{(attendance.error as Error).message}</div>}

        {employeeId && attendance.data && (
          <CalendarGrid
            year={year}
            month={month}
            byDate={byDate}
            onPickDate={(d) => setEditDate(d)}
          />
        )}
      </div>

      {editDate && employeeId && (
        <DayEditModal
          slug={slug}
          employeeId={employeeId}
          date={editDate}
          existing={byDate.get(editDate) ?? null}
          onClose={() => setEditDate(null)}
          onSaved={() => {
            invalidate()
            setEditDate(null)
          }}
        />
      )}

      {showImport && (
        <BulkImportModal
          slug={slug}
          employees={employees.data?.employees ?? []}
          onClose={() => setShowImport(false)}
          onDone={() => {
            invalidate()
            setShowImport(false)
          }}
        />
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Calendar grid — 6 rows × 7 cols, click any day to edit
// ────────────────────────────────────────────────────────────

function CalendarGrid(props: {
  year: number
  month: number
  byDate: Map<string, AttendanceRecord>
  onPickDate: (date: string) => void
}) {
  const firstOfMonth = new Date(props.year, props.month - 1, 1)
  const startDow = firstOfMonth.getDay() // 0=Sun, 6=Sat
  const dim = daysInMonth(props.year, props.month)
  const cells: Array<{ date: string | null; dayNum: number | null }> = []

  // Pad with blanks before the first day so the calendar lines up by weekday.
  for (let i = 0; i < startDow; i++) cells.push({ date: null, dayNum: null })
  for (let d = 1; d <= dim; d++) {
    cells.push({
      date: `${props.year}-${pad(props.month)}-${pad(d)}`,
      dayNum: d,
    })
  }

  return (
    <div className="p-4">
      <div className="grid grid-cols-7 gap-1 mb-1">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="text-[10px] uppercase tracking-wide text-slate-500 text-center py-1">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (!c.date) return <div key={i} className="aspect-square" />
          const r = props.byDate.get(c.date)
          const isSunday = i % 7 === 0
          return (
            <button
              key={c.date}
              onClick={() => props.onPickDate(c.date!)}
              className={`aspect-square rounded border text-left p-1.5 hover:border-brand-500 transition-colors text-xs ${
                r?.isHoliday
                  ? "bg-purple-50 border-purple-200"
                  : isSunday
                  ? "bg-slate-50 border-slate-200"
                  : "bg-white border-slate-200"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className={`font-mono ${r?.isHoliday ? "text-purple-700" : "text-slate-700"}`}>{c.dayNum}</div>
                {r?.isHoliday && <span className="text-[8px] text-purple-600 font-medium">HOL</span>}
              </div>
              {r ? (
                <>
                  <div className={`mt-1 inline-block px-1 py-0.5 text-[9px] rounded ${ATT_STATUS_PILL[r.status]}`}>
                    {r.status.replace("_", " ")}
                  </div>
                  <div className="mt-0.5 text-[10px] font-mono text-slate-700">
                    {Number(r.hoursWorked).toFixed(1)}h
                  </div>
                  {Number(r.overtimeHours) + Number(r.holidayOvertimeHours) > 0 && (
                    <div className="text-[10px] font-mono text-amber-700">
                      +{(Number(r.overtimeHours) + Number(r.holidayOvertimeHours)).toFixed(1)} OT
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-1 text-[10px] text-slate-400">tap to add</div>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Day edit modal
// ────────────────────────────────────────────────────────────

function DayEditModal(props: {
  slug: string
  employeeId: string
  date: string
  existing: AttendanceRecord | null
  onClose: () => void
  onSaved: () => void
}) {
  // For datetime-local inputs we need YYYY-MM-DDTHH:mm format.
  const e = props.existing
  const [clockIn, setClockIn] = useState<string>(e?.clockIn ? e.clockIn.slice(0, 16) : `${props.date}T09:00`)
  const [clockOut, setClockOut] = useState<string>(e?.clockOut ? e.clockOut.slice(0, 16) : `${props.date}T18:00`)
  const [breakMinutes, setBreakMinutes] = useState<string>(String(e?.breakMinutes ?? 60))
  const [status, setStatus] = useState<AttendanceRecord["status"]>(e?.status ?? "present")
  const [notes, setNotes] = useState<string>(e?.notes ?? "")
  const [error, setError] = useState("")

  const upsert = useMutation({
    mutationFn: () =>
      apiTenant(props.slug, "/attendance", {
        method: "POST",
        body: JSON.stringify({
          employeeId: props.employeeId,
          date: props.date,
          // Send absent days with no clock times so the engine zeroes hours.
          clockIn: status === "absent" ? null : new Date(clockIn).toISOString(),
          clockOut: status === "absent" ? null : new Date(clockOut).toISOString(),
          breakMinutes: Number(breakMinutes) || 0,
          status,
          notes: notes.trim() || null,
        }),
      }),
    onSuccess: props.onSaved,
    onError: (err) => setError(err instanceof Error ? err.message : "Failed"),
  })

  const remove = useMutation({
    mutationFn: () => apiTenant(props.slug, `/attendance/${e!.id}`, { method: "DELETE" }),
    onSuccess: props.onSaved,
  })

  function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault()
    setError("")
    if (status !== "absent") {
      if (!clockIn || !clockOut) {
        setError("Clock in and out are required unless status is Absent.")
        return
      }
      if (new Date(clockOut) <= new Date(clockIn)) {
        setError("Clock out must be after clock in.")
        return
      }
    }
    upsert.mutate()
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <form onSubmit={handleSubmit} className="bg-white rounded-lg w-full max-w-md shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-semibold">{props.date}</div>
            {e?.isHoliday && <div className="text-xs text-purple-700">Public holiday</div>}
            {e?.isSunday && <div className="text-xs text-slate-500">Sunday</div>}
          </div>
          <button type="button" onClick={props.onClose} className="text-slate-400 hover:text-slate-700">
            ×
          </button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Status">
            <select
              value={status}
              onChange={(ev) => setStatus(ev.target.value as AttendanceRecord["status"])}
              className="form-input"
            >
              <option value="present">Present</option>
              <option value="late">Late</option>
              <option value="half_day">Half day</option>
              <option value="absent">Absent</option>
            </select>
          </Field>
          {status !== "absent" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Clock in">
                  <input
                    type="datetime-local"
                    value={clockIn}
                    onChange={(ev) => setClockIn(ev.target.value)}
                    className="form-input"
                  />
                </Field>
                <Field label="Clock out">
                  <input
                    type="datetime-local"
                    value={clockOut}
                    onChange={(ev) => setClockOut(ev.target.value)}
                    className="form-input"
                  />
                </Field>
              </div>
              <Field label="Break minutes">
                <input
                  type="number"
                  min="0"
                  max="720"
                  value={breakMinutes}
                  onChange={(ev) => setBreakMinutes(ev.target.value)}
                  className="form-input"
                />
              </Field>
            </>
          )}
          <Field label="Notes">
            <textarea rows={2} value={notes} onChange={(ev) => setNotes(ev.target.value)} className="form-input" />
          </Field>
          {e && (
            <div className="text-[11px] text-slate-500 font-mono bg-slate-50 border border-slate-200 rounded p-2">
              Computed: {Number(e.hoursWorked).toFixed(1)}h worked · {Number(e.regularHours).toFixed(1)}h regular ·{" "}
              {Number(e.overtimeHours).toFixed(1)}h OT · {Number(e.holidayOvertimeHours).toFixed(1)}h holiday OT
            </div>
          )}
          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-between">
          {e ? (
            <button
              type="button"
              onClick={() => {
                if (window.confirm("Delete this attendance record?")) remove.mutate()
              }}
              disabled={remove.isPending}
              className="text-xs text-red-600 hover:text-red-800"
            >
              Delete
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button type="button" onClick={props.onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900">
              Cancel
            </button>
            <button
              type="submit"
              disabled={upsert.isPending}
              className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white text-sm font-medium rounded"
            >
              {upsert.isPending ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Bulk CSV import
// ────────────────────────────────────────────────────────────
// Expected columns: employeeNo, date, clockIn, clockOut, breakMinutes, status, notes
// All but employeeNo + date are optional.

interface ImportRow {
  line: number
  employeeNo: string
  date: string
  clockIn: string
  clockOut: string
  breakMinutes: string
  status: string
  notes: string
}

// Minimal RFC-4180 cell splitter. Handles commas inside quoted fields
// and ""-escaped quotes. Doesn't handle quoted newlines — splitting by
// line first is fine for our use case (no multiline values expected).
function splitCsvLine(line: string): string[] {
  const out: string[] = []
  let cur = ""
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const c = line[i]
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          cur += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        cur += c
      }
    } else {
      if (c === ',') {
        out.push(cur)
        cur = ""
      } else if (c === '"' && cur === "") {
        inQuotes = true
      } else {
        cur += c
      }
    }
  }
  out.push(cur)
  return out.map((s) => s.trim())
}

function parseCsv(text: string): { rows: ImportRow[]; error: string | null } {
  const lines = text.split(/\r?\n/).filter((l) => l.trim())
  if (lines.length === 0) return { rows: [], error: "File is empty." }
  const header = splitCsvLine(lines[0]).map((h) => h.toLowerCase())
  const idx = (name: string) => header.indexOf(name)
  const i = {
    employeeNo: idx("employeeno"),
    date: idx("date"),
    clockIn: idx("clockin"),
    clockOut: idx("clockout"),
    breakMinutes: idx("breakminutes"),
    status: idx("status"),
    notes: idx("notes"),
  }
  if (i.employeeNo === -1 || i.date === -1) {
    return { rows: [], error: "CSV must have at least 'employeeNo' and 'date' columns." }
  }
  const rows: ImportRow[] = []
  for (let n = 1; n < lines.length; n++) {
    const cells = splitCsvLine(lines[n])
    rows.push({
      line: n + 1,
      employeeNo: cells[i.employeeNo] ?? "",
      date: cells[i.date] ?? "",
      clockIn: i.clockIn >= 0 ? cells[i.clockIn] ?? "" : "",
      clockOut: i.clockOut >= 0 ? cells[i.clockOut] ?? "" : "",
      breakMinutes: i.breakMinutes >= 0 ? cells[i.breakMinutes] ?? "" : "",
      status: i.status >= 0 ? cells[i.status] ?? "" : "",
      notes: i.notes >= 0 ? cells[i.notes] ?? "" : "",
    })
  }
  return { rows, error: null }
}

function BulkImportModal(props: {
  slug: string
  employees: Employee[]
  onClose: () => void
  onDone: () => void
}) {
  const [parsed, setParsed] = useState<ImportRow[]>([])
  const [parseError, setParseError] = useState<string | null>(null)
  const [progress, setProgress] = useState<{ done: number; ok: number; failed: number; failures: string[] } | null>(
    null,
  )
  const [running, setRunning] = useState(false)

  const empByNo = useMemo(() => new Map(props.employees.map((e) => [e.employeeNo, e])), [props.employees])

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    file.text().then((text) => {
      const { rows, error } = parseCsv(text)
      setParsed(rows)
      setParseError(error)
      setProgress(null)
    })
  }

  async function runImport() {
    if (running) return
    setRunning(true)
    const out = { done: 0, ok: 0, failed: 0, failures: [] as string[] }
    for (const row of parsed) {
      out.done++
      const emp = empByNo.get(row.employeeNo)
      if (!emp) {
        out.failed++
        out.failures.push(`Line ${row.line}: unknown employeeNo "${row.employeeNo}"`)
        setProgress({ ...out })
        continue
      }
      try {
        // Compose full ISO strings if just times were given.
        function makeIso(s: string): string | null {
          if (!s) return null
          if (s.includes("T")) return new Date(s).toISOString()
          // "HH:MM" → row.date + that time, treated as local TZ then to ISO.
          if (/^\d{1,2}:\d{2}$/.test(s)) return new Date(`${row.date}T${s}:00`).toISOString()
          return new Date(s).toISOString()
        }
        await apiTenant(props.slug, "/attendance", {
          method: "POST",
          body: JSON.stringify({
            employeeId: emp.id,
            date: row.date,
            clockIn: makeIso(row.clockIn),
            clockOut: makeIso(row.clockOut),
            breakMinutes: row.breakMinutes ? Number(row.breakMinutes) : 0,
            status: row.status || "present",
            notes: row.notes || null,
          }),
        })
        out.ok++
      } catch (err) {
        out.failed++
        const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "unknown"
        out.failures.push(`Line ${row.line} (${row.employeeNo} ${row.date}): ${msg}`)
      }
      setProgress({ ...out })
    }
    setRunning(false)
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="font-semibold">Bulk import attendance</div>
          <button type="button" onClick={props.onClose} className="text-slate-400 hover:text-slate-700">
            ×
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs text-slate-500">
            Required columns: <code className="font-mono">employeeNo, date</code>. Optional:{" "}
            <code className="font-mono">clockIn, clockOut, breakMinutes, status, notes</code>. Existing rows are
            replaced (idempotent).
          </div>
          <input type="file" accept=".csv,text/csv" onChange={handleFile} className="text-sm" />
          {parseError && <div className="text-sm text-red-600">{parseError}</div>}

          {parsed.length > 0 && (
            <div className="text-xs text-slate-600">
              Parsed <strong>{parsed.length}</strong> row{parsed.length === 1 ? "" : "s"}.
              <div className="mt-2 max-h-48 overflow-y-auto border border-slate-200 rounded">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
                    <tr>
                      <th className="px-2 py-1 text-left">Line</th>
                      <th className="px-2 py-1 text-left">Emp #</th>
                      <th className="px-2 py-1 text-left">Date</th>
                      <th className="px-2 py-1 text-left">In/Out</th>
                      <th className="px-2 py-1 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.slice(0, 10).map((r) => (
                      <tr key={r.line} className="border-t border-slate-100">
                        <td className="px-2 py-1 font-mono">{r.line}</td>
                        <td className="px-2 py-1 font-mono">
                          {r.employeeNo}
                          {!empByNo.has(r.employeeNo) && <span className="text-red-600 ml-1">!</span>}
                        </td>
                        <td className="px-2 py-1 font-mono">{r.date}</td>
                        <td className="px-2 py-1 font-mono">
                          {r.clockIn || "—"} / {r.clockOut || "—"}
                        </td>
                        <td className="px-2 py-1">{r.status || "present"}</td>
                      </tr>
                    ))}
                    {parsed.length > 10 && (
                      <tr>
                        <td colSpan={5} className="px-2 py-1 text-slate-400 text-center italic">
                          …{parsed.length - 10} more
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {progress && (
            <div className="text-sm">
              <div>
                <strong>{progress.done}</strong> of {parsed.length} processed · {progress.ok} ok · {progress.failed}{" "}
                failed
              </div>
              {progress.failures.length > 0 && (
                <div className="mt-2 max-h-40 overflow-y-auto border border-red-200 bg-red-50 rounded p-2 text-xs text-red-700 font-mono">
                  {progress.failures.map((f, i) => (
                    <div key={i}>{f}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={props.onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900">
            Close
          </button>
          {progress?.done === parsed.length && progress.failed === 0 && (
            <button
              type="button"
              onClick={props.onDone}
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded"
            >
              Done
            </button>
          )}
          {parsed.length > 0 && !progress && (
            <button
              type="button"
              onClick={runImport}
              disabled={running}
              className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white text-sm font-medium rounded"
            >
              {running ? "Importing…" : `Import ${parsed.length} rows`}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function Field(props: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-600 mb-1">{props.label}</div>
      {props.children}
    </label>
  )
}
